import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CMP_HIDERS } from './cmp-consent-hiders';
import { hideUnlabelledCookieBanners } from './hide-unlabelled-cookie-banners';

// ~30 KB of provider CSS — joined once at module load, not per render.
const CMP_HIDER_CSS = CMP_HIDERS.map((h) => h.css).join('\n');

/**
 * Removes every element whose class list contains the given class, children
 * included. Matching closing tags are found with a depth counter over the
 * same tag name, because these containers hold nested elements — a lazy
 * `[\s\S]*?<\/div>` regex would stop at the first inner close and leave
 * orphaned markup. A container whose closing tag is never found (malformed
 * HTML) is left untouched.
 */
function removeElementsByClass(html: string, className: string): string {
  const openRe = new RegExp(
    `<([a-z][a-z0-9-]*)\\b(?=[^>]*\\bclass\\s*=\\s*["'][^"']*(?<![\\w-])${className}(?![\\w-])[^"']*["'])[^>]*>`,
    'gi'
  );

  let result = html;
  let match: RegExpExecArray | null;
  while ((match = openRe.exec(result))) {
    const tag = match[1];
    const tagRe = new RegExp(`<${tag}\\b[^>]*>|<\\/${tag}\\s*>`, 'gi');
    tagRe.lastIndex = match.index + match[0].length;
    let depth = 1;
    let end = -1;
    let found: RegExpExecArray | null;
    while (depth > 0 && (found = tagRe.exec(result))) {
      depth += found[0][1] === '/' ? -1 : 1;
      if (depth === 0) end = found[0].length + found.index;
    }
    if (end === -1) continue;

    result = result.slice(0, match.index) + result.slice(end);
    openRe.lastIndex = match.index;
  }
  return result;
}

/** Clone slides the slider libraries generate at runtime, by class. */
const SLIDER_CLONE_CLASSES = ['splide__slide--clone', 'swiper-slide-duplicate', 'slick-cloned'];
/** The tracks those libraries translate, by class. */
const SLIDER_TRACK_CLASS_RE = /\b(?:splide__list|swiper-wrapper|slick-track)\b/;

/**
 * Captures made before the backend dropped them carry a slider's runtime
 * state: the clone slides Splide/Swiper/Slick generated on the live page,
 * and the track's inline transform. The preview runs the page's own
 * scripts, so the slider mounts again over a track that already holds a
 * clone set — twice the clones, every clone id twice, and a model that no
 * longer matches the DOM (a modjo.ai marquee jumped between positions and
 * blank frames on every tick). Drop the clones and the stale transform so
 * the slider mounts on the same markup it did on the live page. The backend
 * now does this at capture time (new analyses); this covers older captures.
 */
export function stripSliderRuntimeState(html: string): string {
  let result = html;
  for (const className of SLIDER_CLONE_CLASSES) {
    if (result.includes(className)) result = removeElementsByClass(result, className);
  }
  return result.replace(
    /<[a-z][a-z0-9-]*\b[^>]*\b(?:splide__list|swiper-wrapper|slick-track)\b[^>]*>/gi,
    (tag: string) =>
      SLIDER_TRACK_CLASS_RE.test(tag.match(/\bclass\s*=\s*["']([^"']*)["']/i)?.[1] ?? '')
        ? tag.replace(
            /(\sstyle\s*=\s*)("([^"]*)"|'([^']*)')/i,
            (m: string, prefix: string, _q: string, dq?: string, sq?: string) => {
              const value = dq ?? sq ?? '';
              const quote = dq !== undefined ? '"' : "'";
              const cleaned = value
                .replace(/(?:^|;)\s*transform\s*:[^;]*/gi, '')
                .replace(/^\s*;\s*/, '');
              return `${prefix}${quote}${cleaned}${quote}`;
            }
          )
        : tag
  );
}

/**
 * Sanitizes HTML by removing all JavaScript code and disabling navigation actions
 */
function sanitizeHtml(html: string): string {
  let sanitized = html;

  // Remove Elementor motion-effect containers: they duplicate the section
  // background into transform-animated layers, which render misplaced in the
  // static preview.
  sanitized = removeElementsByClass(sanitized, 'elementor-motion-effects-container');

  // Remove <script type="application/json"> blocks (e.g. Nuxt payload)
  sanitized = sanitized.replace(
    /<script[^>]*type\s*=\s*["']application\/json["'][^>]*>[\s\S]*?<\/script>/gi,
    ''
  );

  // Remove data: URLs on <a> tags that could execute code (e.g. data:text/html)
  // Scoped to <a> only to avoid breaking SVG <image> or <use> elements with data: URIs
  sanitized = sanitized.replace(
    /(<a\b[^>]*?)href\s*=\s*["']data:[^"']*["']/gi,
    '$1href="#"'
  );

  // Remove link tags that prefetch/preload/modulepreload scripts
  sanitized = sanitized.replace(
    /<link[^>]*rel\s*=\s*["'](?:prefetch|preload|modulepreload)["'][^>]*as\s*=\s*["']script["'][^>]*>/gi,
    ''
  );
  sanitized = sanitized.replace(
    /<link[^>]*as\s*=\s*["']script["'][^>]*rel\s*=\s*["'](?:prefetch|preload|modulepreload)["'][^>]*>/gi,
    ''
  );
  // Remove modulepreload links (always scripts, no as= needed)
  sanitized = sanitized.replace(
    /<link[^>]*rel\s*=\s*["']modulepreload["'][^>]*>/gi,
    ''
  );

  // Normalize script loading by origin. A synchronous `<script src>` blocks
  // the HTML parser until the request finishes, so one slow/hanging
  // third-party script stalls the whole page render — those get `async`.
  //
  // First-party scripts (tinct.ai / localhost — the page's own captured JS,
  // served by our fast resource proxy) get the OPPOSITE treatment: any
  // `async` is STRIPPED. The capture backend marks originally-synchronous
  // scripts `async` in the stored snapshot, which destroys their document
  // order — captured sites depend on it (e.g. itsme's HubSpot theme loads
  // `template_main` sync and `template_nav` deferred; template_nav calls
  // `act.debounce(...)` from template_main at listener-bind time. As async,
  // whenever template_main lost the race the binding threw mid-loop and the
  // mega menu could open but never close). Stripping `async` restores the
  // original sync-or-defer semantics; `defer` is left alone since it already
  // preserves order. Same trust split as the stylesheet handling below.
  sanitized = sanitized.replace(
    /<script\b[^>]*\bsrc\s*=[^>]*>/gi,
    (tag: string) => {
      const src = tag.match(/\bsrc\s*=\s*["']?([^"'\s>]*)/i)?.[1] ?? '';
      if (/tinct\.ai|localhost/i.test(src)) {
        return tag.replace(
          /\s+async(\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?(?=[\s>])/gi,
          ''
        );
      }
      if (/[^>]*\s(?:async|defer)[\s=>]/i.test(tag)) return tag;
      return tag.replace(/^<script\b/i, '<script async');
    }
  );

  // Make external stylesheets non-render-blocking. A `<link rel="stylesheet">`
  // blocks first paint until it loads, so a slow/hanging stylesheet leaves the
  // preview blank. Load it at "print" priority (not render-blocking) then flip
  // it to "all" once it arrives. The height is re-measured on the poll loop, so
  // late-applied CSS is picked up. Skips links that already declare a media.
  //
  // First-party stylesheets (tinct.ai / localhost) are trusted and fast, so we
  // keep them render-blocking to avoid a flash of unstyled content; only
  // third-party sheets (which may hang) are deferred.
  sanitized = sanitized.replace(
    /<link\b(?![^>]*\bmedia\s*=)([^>]*\brel\s*=\s*["']stylesheet["'][^>]*)>/gi,
    (match: string, attrs: string) => {
      const href = match.match(/\bhref\s*=\s*["']([^"']*)["']/i)?.[1] ?? '';
      if (/tinct\.ai|localhost/i.test(href)) return match;
      return `<link${attrs} media="print" onload="this.media='all'">`;
    }
  );

  // Empty out Lottie animation containers (class "lottie-animation" + a
  // data-src). These hold a heavy inlined/injected SVG that Lottie animates on
  // a perpetual rAF loop — wasted work in a static preview. We keep the <div>
  // (and its attributes/layout box) but strip its children. The two lookaheads
  // match class/data-src in either order; assumes the container holds no nested
  // <div> (its content is the SVG), so the first </div> closes it.
  // sanitized = sanitized.replace(
  //   /(<div\b(?=[^>]*\bclass\s*=\s*["'][^"']*\blottie-animation\b)(?=[^>]*\bdata-src\b)[^>]*>)[\s\S]*?<\/div>/gi,
  //   '$1</div>'
  // );

  // Neutralize history.replaceState/pushState calls
  sanitized = sanitized.replace(/history\s*\.\s*replaceState/gi, 'void 0//');
  sanitized = sanitized.replace(/history\s*\.\s*pushState/gi, 'void 0//');

  // Neutralize all <a> navigation: void href + inline onclick guard + no drag
  sanitized = sanitized.replace(
    /(<a\b[^>]*?)href\s*=\s*["'][^"']*["']/gi,
    '$1href="javascript:void 0" onclick="return false" draggable="false"'
  );

  // Add tabindex="-1" to focusable elements to prevent keyboard focus
  sanitized = sanitized.replace(
    /<(a|button|input|select|textarea)\b/gi,
    '<$1 tabindex="-1"'
  );

  // Remove meta refresh tags
  sanitized = sanitized.replace(
    /<meta[^>]*http-equiv\s*=\s*["']refresh["'][^>]*>/gi,
    ''
  );

  return sanitized;
}

/**
 * Injects a Content-Security-Policy to block all script fetching/execution,
 * and CSS to disable interaction while preserving visual rendering.
 */
function injectIsolationStyles(html: string, scrollChaining: boolean): string {
  const isolationHead = `
    <meta http-equiv="Content-Security-Policy" content="form-action 'none';">
    <style data-tinct-isolation>
      ${CMP_HIDER_CSS}
      a, button, input, select, textarea, form {
        pointer-events: none !important;
        -webkit-user-select: none !important;
        user-select: none !important;
      }
      [data-tinct-overlay] a,
      [data-tinct-overlay] button,
      [data-tinct-overlay] input,
      [data-tinct-overlay] select,
      [data-tinct-overlay] textarea,
      [data-tinct-overlay] form {
        pointer-events: auto !important;
        -webkit-user-select: auto !important;
        user-select: auto !important;
      }
      a:focus, button:focus, input:focus, select:focus, textarea:focus, form:focus {
        outline: none !important;
        box-shadow: none !important;
      }
      body { -webkit-user-select: none !important; user-select: none !important; }
      /* A trackpad swipe that starts over the preview must not chain out of
         the iframe into the host's root scroller, where Chrome turns a
         horizontal overscroll into back/forward navigation. In flow mode the
         iframe is as tall as its content and never scrolls: the host document
         does, so vertical scrolls have to chain out (touch scrolling on a
         phone chains natively — nothing forwards it like the studio's wheel
         forwarder does). */
      html, body { overscroll-behavior: none !important; }
      ${scrollChaining ? 'html:root:root:root:root, html:root:root:root:root body { overscroll-behavior-y: auto !important; }' : ''}
      /* Inline text edition (useInlineTextEditor): the element being edited
         needs a caret (user-select) and clickable text inside links/buttons
         (navigation is already neutralized by sanitizeHtml). The outline is
         the editing affordance; the block-ring overlay is hidden meanwhile so
         it doesn't fight the caret. */
      [data-tinct-editing], [data-tinct-editing] * {
        -webkit-user-select: text !important;
        user-select: text !important;
        cursor: text !important;
      }
      [data-tinct-editing] a, [data-tinct-editing] button {
        pointer-events: auto !important;
      }
      [data-tinct-editing] {
        outline: 2px solid #6366f1 !important;
        /* Drawn inside the box: page containers routinely clip overflow
           (Framer/Webflow wrappers), and text that fills its container has
           no room outside its box — an outset ring lost every edge but the
           bottom. */
        outline-offset: -2px !important;
        /* On top of any sibling layer (a stacked copy of the text blended
           over it), so the pointer, caret and selection reach the element
           being edited. The editor makes it positioned when needed. */
        z-index: 2147483000 !important;
      }
      [data-tinct-editing] > [data-tinct-overlay] { visibility: hidden !important; }
      /* Other copies of the edited text (same selector): out of the way for
         the duration of the edit; they receive the saved text on re-apply. */
      [data-tinct-editing-twin] { visibility: hidden !important; }
      /* Submitted and waiting for the backend to validate/process the text:
         the block is locked (no caret) until the save settles. The visual
         feedback is the "Applying changes..." overlay (SavingOverlay), which
         draws its own frame — so the editing outline comes off. */
      [data-tinct-saving], [data-tinct-saving] * {
        cursor: progress !important;
        -webkit-user-select: none !important;
        user-select: none !important;
      }
      [data-tinct-saving] {
        outline: none !important;
      }
    </style>
  `;

  // Inject after <head> if present, otherwise before </html> or at the start
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, `$&${isolationHead}`);
  }
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html[^>]*>/i, `$&<head>${isolationHead}</head>`);
  }
  return isolationHead + html;
}

/** Backend-sanitized preview: the first `<script>` in `<head>` carries this attribute. */
function hasPreviewGuard(html: string): boolean {
  return /<script\b[^>]*\bdata-tinct-preview-guard\b/i.test(html);
}

/** `type` values under which a <script> is code the browser runs. */
const EXECUTABLE_SCRIPT_TYPE_RE =
  /^(?:module|(?:text|application)\/(?:x-)?(?:java|ecma)script|text\/jsx?)?$/i;

/**
 * The page without the scripts the browser would run: external and inline
 * code is dropped, data blocks (`application/json`, `application/ld+json`,
 * the capture's `text/comment`…) and our own `data-tinct-*` scripts (the
 * preview guard) stay. What a preview falls back to once the page's own
 * code proves it cannot be trusted to keep the document alive — see
 * `useShadowDom` (a page that reloads itself).
 */
export function stripExecutableScripts(html: string): string {
  return html.replace(
    /<script\b([^>]*)>[\s\S]*?<\/script\s*>/gi,
    (block: string, attrs: string) => {
      if (/\bdata-tinct-/i.test(attrs)) return block;
      const type = attrs.match(/\btype\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const value = (type?.[1] ?? type?.[2] ?? type?.[3] ?? '').trim();
      return EXECUTABLE_SCRIPT_TYPE_RE.test(value) ? '' : block;
    }
  );
}

/**
 * Captures whose page reloaded itself in a preview (see `useShadowDom`). A
 * page that did it once does it on every mount — each tab switch or artboard
 * mode change would pay the load-then-reload flash again — so the next mount
 * of the same html renders script-less from the start. Module-level on
 * purpose: the memory must outlive the hook instances.
 */
const SELF_RELOADING_HTML = new Set<string>();

/** Whether the iframe's current document is a reload the page triggered itself. */
function isSelfReload(iframe: HTMLIFrameElement): boolean {
  try {
    const entry = iframe.contentWindow?.performance?.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return entry?.type === 'reload';
  } catch {
    return false;
  }
}

/**
 * Artboards (canvas) size the iframe to its content, so the document has no
 * real viewport: `vh` would resolve against the whole page height and any
 * viewport-sized section (hero, sticky scrollytelling) balloons — feeding
 * back into the content height on every measurement. Pins viewport units in
 * the inline stylesheets to the dimension key's height, and Framer's own
 * viewport variable with them, before the first layout. External stylesheets
 * are out of reach for this string pass — `pinExternalStylesheets` covers
 * them at runtime.
 */
/** `100vh`, `50dvh`, `.5svh`… (not inside an identifier such as `--my-vh`). */
const VIEWPORT_UNIT_RE = /(?<![\w.-])(\d*\.?\d+)[sdl]?vh\b/g;

/**
 * Rewrites every viewport-height unit inside the declaration blocks of a CSS
 * string to pixels. Selectors are left alone on purpose: Tailwind arbitrary
 * values put the unit in the class name (`.min-h-\[calc\(100vh-71px\)\]`),
 * and a rewritten selector would no longer match anything.
 */
export function pinViewportUnitsInCss(css: string, viewportHeight: number): string {
  const pinDeclarations = (block: string) =>
    block.replace(
      VIEWPORT_UNIT_RE,
      (_, value: string) => `${(parseFloat(value) * viewportHeight) / 100}px`
    );
  return css.replace(/\{[^{}]*\}/g, pinDeclarations);
}

/** A declaration list (a `style` attribute's value) with its viewport-height units pinned. */
function pinViewportUnitsInDeclarations(css: string, viewportHeight: number): string {
  return css.replace(
    VIEWPORT_UNIT_RE,
    (_, value: string) => `${(parseFloat(value) * viewportHeight) / 100}px`
  );
}

/**
 * Pins the viewport-height units of every inline `style="…"` attribute.
 * Builders put them there too — Framer's page wrapper ships as
 * `style="min-height:100vh"`, which in an auto-height artboard is the
 * feedback loop in its purest form: the wrapper is at least as tall as the
 * iframe, the iframe as tall as the page, the page as tall as the wrapper
 * plus whatever follows it — and grows by that remainder on every measure.
 */
export function pinViewportUnitsInStyleAttributes(html: string, viewportHeight: number): string {
  return html.replace(
    /(\sstyle\s*=\s*)("([^"]*)"|'([^']*)')/gi,
    (match: string, prefix: string, _quoted: string, dq?: string, sq?: string) => {
      const value = dq ?? sq ?? '';
      VIEWPORT_UNIT_RE.lastIndex = 0;
      if (!VIEWPORT_UNIT_RE.test(value)) return match;
      const quote = dq !== undefined ? '"' : "'";
      return `${prefix}${quote}${pinViewportUnitsInDeclarations(value, viewportHeight)}${quote}`;
    }
  );
}

/**
 * The runtime counterpart of `pinViewportUnitsInStyleAttributes`: a style a
 * script set (or reset) after the document was parsed. Runs on every
 * measurement, so the pass is a single attribute-substring query.
 */
function pinInlineStyleAttributes(doc: Document, viewportHeight: number): void {
  doc.querySelectorAll<HTMLElement>('[style*="vh"]').forEach((el) => {
    const value = el.getAttribute('style') ?? '';
    VIEWPORT_UNIT_RE.lastIndex = 0;
    if (!VIEWPORT_UNIT_RE.test(value)) return;
    el.setAttribute('style', pinViewportUnitsInDeclarations(value, viewportHeight));
  });
}

function pinViewportUnits(html: string, viewportHeight: number): string {
  const pinned = pinViewportUnitsInStyleAttributes(
    html.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, (block) =>
      pinViewportUnitsInCss(block, viewportHeight)
    ),
    viewportHeight
  );
  // An artboard is as tall as its content, so it never scrolls on purpose —
  // but the document still overflows its viewport: by the sub-pixel the
  // iframe height rounds off, and by more while the height trails a layout
  // change (ResizeObserver → React → paint). Either way the page's own
  // scrollbar shows up and every line reflows around its gutter. Clip the
  // root and drop the gutter; the canvas does the scrolling.
  //
  // The `:root` stack is specificity: the CMP hider (injectIsolationStyles)
  // undoes consent-banner scroll locks with class-qualified
  // `html.noScroll … { overflow: visible !important }` rules, and `html`
  // alone loses to them.
  const pin =
    `<style data-tinct-viewport-pin>` +
    `:root{--framer-viewport-height:${viewportHeight}px !important}` +
    `html:root:root:root:root{overflow:hidden !important;scrollbar-width:none !important}` +
    `</style>`;
  return /<head[^>]*>/i.test(pinned) ? pinned.replace(/<head[^>]*>/i, `$&${pin}`) : pin + pinned;
}

/**
 * Makes relative `url(...)` references in a rule absolute against the sheet
 * they came from, so the rule keeps resolving once it lives in an inline
 * <style> in the document instead of in that sheet.
 */
export function absolutizeCssUrls(css: string, sheetHref: string): string {
  const resolve = (path: string): string => {
    try {
      return new URL(path, sheetHref).href;
    } catch {
      return path;
    }
  };
  // `@import "relative.css"` (no url()): the only import form the url()
  // pass below does not see. With the original link disabled, the inline
  // copy is the sheet's only representation, so it must resolve too.
  const withImports = css.replace(
    /@import\s+(['"])(?!data:|blob:|https?:|\/\/)([^'"]+)\1/g,
    (match, quote: string, path: string) => `@import ${quote}${resolve(path)}${quote}`
  );
  return withImports.replace(
    /url\(\s*(['"]?)(?!data:|blob:|https?:|\/\/|#)([^'")]+)\1\s*\)/g,
    (match, quote: string, path: string) => {
      try {
        return `url(${quote}${new URL(path, sheetHref).href}${quote})`;
      } catch {
        return match;
      }
    }
  );
}

/** Elements whose inline visibility this pass has already judged (the value it judged). */
const RESTING_VISIBILITY_ATTR = 'data-tinct-resting';

/**
 * How many of the document's stylesheets currently apply. A sheet that
 * arrives late changes this count: `sanitizeHtml` loads third-party ones at
 * `media="print"` and flips them to `all` once fetched, and pages inject
 * their own `<style>` at runtime (bulldozer-collective.com's mobile nav
 * rules are appended to `<head>` by a script). A verdict measured before one
 * of those landed has to be taken again, so the count is part of what a
 * judged element remembers.
 */
function applyingStyleSheetCount(doc: Document): number {
  const win = doc.defaultView;
  return Array.from(doc.styleSheets).filter((sheet) => {
    const media = sheet.media?.mediaText;
    if (!media || media === 'all') return true;
    try {
      return win?.matchMedia(media).matches ?? true;
    } catch {
      return true;
    }
  }).length;
}

/** Whether the element's own CSS, inline declarations aside, keeps it out of sight. */
function isHiddenByStylesheets(el: Element, win: Window): boolean {
  const cs = win.getComputedStyle(el);
  return cs.visibility === 'hidden' || cs.visibility === 'collapse' || parseFloat(cs.opacity) === 0;
}

/**
 * Drops inline `opacity` / `visibility` that shows an out-of-flow layer the
 * page's own stylesheets hide at this artboard's width.
 *
 * A capture is a desktop DOM (one capture per version, rendered at every
 * artboard width), so at 800 or 400 the page's script has to convert it into
 * the mobile layout — and until it does, or if it never does, the desktop
 * state of a nav panel shows. Two cases seen: bulldozer-collective.com's
 * mobile nav list, which its GSAP code leaves at `opacity: 1; visibility:
 * visible` while the site's own `@media (max-width: 991px)` rule (not
 * `!important`) hides it, and mobiskill.fr's menu panel, which the capture
 * stores that way. Both cover the artboard with a white panel; the second
 * never recovers, the first only when the page's `DOMContentLoaded` init
 * runs, up to half a minute later.
 *
 * The rule is narrow on purpose:
 * - only `position: fixed` / `absolute` elements, so nothing in the document
 *   flow can ever be removed from sight — a scroll-revealed section that its
 *   CSS starts hidden keeps the inline opacity that reveals it;
 * - only inline values that make the element VISIBLE, so a page hiding
 *   something itself is never overridden;
 * - and only when removing them actually leaves the element hidden. When the
 *   page opens that layer for real (`[data-menu-open="true"] … { opacity: 1
 *   !important }`), the stylesheets keep it visible, the inline values are
 *   put back, and the pass is a no-op.
 *
 * Idempotent: a value that survives the pass is recorded on the element with
 * the number of stylesheets that were applying when it was judged, so the
 * same one is never measured twice and a sheet arriving later re-opens the
 * question. Nothing is remembered before the document is complete (styles
 * are still landing) or once a value is dropped (the page writing it again
 * is judged again).
 */
export function enforceRestingVisibility(doc: Document): void {
  const win = doc.defaultView;
  if (!win) return;

  // Before `complete` the cascade is still changing, so no verdict is kept.
  const settled = doc.readyState === 'complete';
  const sheets = applyingStyleSheetCount(doc);

  doc.querySelectorAll<HTMLElement>('[style*="opacity"], [style*="visibility"]').forEach((el) => {
    const style = el.style;
    if (!style || el.closest('[data-tinct-overlay]')) return;

    const opacity = style.opacity;
    const visibility = style.visibility;
    if (!opacity && !visibility) return;
    // Inline values that hide it already: the page's own doing, left alone.
    if (opacity && parseFloat(opacity) === 0) return;
    if (visibility && visibility !== 'visible') return;

    const judged = `${opacity}|${visibility}|${sheets}`;
    if (settled && el.getAttribute(RESTING_VISIBILITY_ATTR) === judged) return;
    const remember = () => {
      if (settled) el.setAttribute(RESTING_VISIBILITY_ATTR, judged);
    };

    const position = win.getComputedStyle(el).position;
    if (position !== 'fixed' && position !== 'absolute') {
      remember();
      return;
    }

    const priorities = [style.getPropertyPriority('opacity'), style.getPropertyPriority('visibility')];
    style.removeProperty('opacity');
    style.removeProperty('visibility');
    if (isHiddenByStylesheets(el, win)) {
      // Dropped: nothing left to remember, and a value the page writes again
      // (its animation re-running) has to be judged again.
      el.removeAttribute(RESTING_VISIBILITY_ATTR);
      return;
    }

    // The page's CSS shows it too: the inline values were not the reason.
    if (opacity) style.setProperty('opacity', opacity, priorities[0]);
    if (visibility) style.setProperty('visibility', visibility, priorities[1]);
    remember();
  });
}

const FIRST_PARTY_RESOURCE_RE = /tinct\.ai|localhost/i;
const EXTERNAL_PIN_ATTR = 'data-tinct-viewport-pinned';

/**
 * The runtime half of viewport pinning: `pinViewportUnits` only reaches the
 * inline <style> blocks of the capture, but builders like Tailwind / Next.js
 * ship `min-height: 100vh` in EXTERNAL stylesheets. In an auto-height
 * artboard those resolve against the whole page height, so the page grows
 * to fill itself (a 6k-pixel page measured 24k). Every first-party sheet
 * (the capture's proxied resources, served with CORS) is fetched once and,
 * when it uses viewport-height units, REPLACED in place by the same sheet
 * with those units pinned: a <style> carrying the whole rewritten text right
 * after the link, and the link disabled. Same position, same @media and
 * @layer structure, only the `vh` values differ — so the cascade is exactly
 * the page's. (Appending only the pinned declarations after the link, the
 * previous approach, outranked the sheet's own later overrides: tellent.com
 * styles its nav as a `height: 100vh` mobile panel and switches it to
 * `height: auto` under `@media (min-width: 992px)`; the appended
 * `height: 900px` beat that override and the nav bar sat in the middle of
 * the hero at 1400.) Third-party sheets are left alone — no CORS, and the
 * capture proxies what matters. Failures are silent: the page just keeps
 * today's behaviour.
 */
export function pinExternalStylesheets(doc: Document, viewportHeight: number): void {
  const links = doc.querySelectorAll<HTMLLinkElement>(
    `link[rel="stylesheet"][href]:not([${EXTERNAL_PIN_ATTR}])`
  );
  links.forEach((link) => {
    link.setAttribute(EXTERNAL_PIN_ATTR, 'pending');
    if (!FIRST_PARTY_RESOURCE_RE.test(link.href)) {
      link.setAttribute(EXTERNAL_PIN_ATTR, 'skipped');
      return;
    }
    fetch(link.href, { mode: 'cors', credentials: 'omit' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((css) => {
        const pinned = pinStylesheetText(css, viewportHeight, link.href);
        if (pinned === null || !link.isConnected) {
          link.setAttribute(EXTERNAL_PIN_ATTR, 'none');
          return;
        }
        const style = doc.createElement('style');
        style.setAttribute('data-tinct-viewport-pin', link.href);
        // The replacement keeps the link's media (a print sheet stays a print
        // sheet), except for a link the deferred-loading rewrite in
        // sanitizeHtml put at `media="print"` until fetched: that is a screen
        // sheet, recognisable by the onload handler that flips its media.
        const deferred = /media/.test(link.getAttribute('onload') ?? '');
        if (link.media && !deferred) style.setAttribute('media', link.media);
        style.textContent = pinned;
        link.after(style);
        link.disabled = true;
        link.setAttribute(EXTERNAL_PIN_ATTR, 'done');
      })
      .catch(() => link.setAttribute(EXTERNAL_PIN_ATTR, 'failed'));
  });
}

/**
 * A stylesheet's text with its viewport-height units pinned and its
 * relative urls made absolute against the sheet's own href, ready to stand
 * in for the sheet inline. Null when the sheet has no such unit (nothing to
 * replace).
 */
export function pinStylesheetText(css: string, viewportHeight: number, sheetHref: string): string | null {
  VIEWPORT_UNIT_RE.lastIndex = 0;
  if (!VIEWPORT_UNIT_RE.test(css)) return null;
  return absolutizeCssUrls(pinViewportUnitsInCss(css, viewportHeight), sheetHref);
}

export type UseShadowDomOptions = {
  html: string;
  /** Artboard mode: pins the document's `vh` units to this height (see pinViewportUnits). */
  viewportHeight?: number;
  /**
   * Let vertical scrolls chain out of the iframe into the host document.
   * For flow mode (auto-height iframe inside a scrolling document); default
   * off, see the isolation styles.
   */
  scrollChaining?: boolean;
};

export type UseShadowDomResult = {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  contentDocument: Document | null;
  contentHeight: number;
};

/**
 * Hook that manages an iframe with srcdoc for rendering HTML content.
 * Uses sandbox to prevent script execution and navigation.
 * Returns refs to the iframe and its contentDocument for DOM queries.
 */
export function useShadowDom({
  html,
  viewportHeight,
  scrollChaining = false,
}: UseShadowDomOptions): UseShadowDomResult {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [contentDocument, setContentDocument] = useState<Document | null>(null);
  const [contentHeight, setContentHeight] = useState(0);

  // The html whose page reloaded itself inside the preview. A preview is a
  // static capture; nothing in it should navigate — but a page's own code
  // can (gutenberg-technology.com's HubSpot pixel loader calls
  // `location.reload()` when its consent state flips, which in the preview
  // it does on every load: the artboard reloads twice a second, forever).
  // `location.reload` cannot be stubbed (non-configurable) and the sandbox
  // has no flag for it, so the page is re-rendered without its scripts
  // instead. Keyed on the html so a new capture gets its scripts back.
  const [selfReloadedHtml, setSelfReloadedHtml] = useState<string | null>(null);
  const scriptsStripped = selfReloadedHtml === html || SELF_RELOADING_HTML.has(html);
  const freezeScripts = useCallback(() => {
    SELF_RELOADING_HTML.add(html);
    setSelfReloadedHtml(html);
  }, [html]);

  // The document a `pagehide` listener is attached to, and whether the next
  // unload is our own srcdoc write (ignored) rather than the page navigating.
  const listenedDocumentRef = useRef<Document | null>(null);
  const ownNavigationRef = useRef(false);
  const disposedRef = useRef(false);
  useEffect(
    () => () => {
      // Unmount removes the iframe and unloads its document: not the page's doing.
      disposedRef.current = true;
    },
    []
  );

  // A dozen full-document regex passes over a multi-hundred-KB string: keyed
  // on the html so re-renders (canvas pan/zoom ticks, parent state) don't
  // pay it again. The srcdoc effect below keys on the resulting string.
  //
  // Captures sanitized by the backend (preview-iframe contract) carry a guard
  // script first in <head>: links and forms are already inert and the script
  // set is curated there, so the local pass — which would re-order scripts
  // (async) and strip payloads — is skipped for them. The isolation styles
  // (CMP hider, overlay interactivity) still apply.
  const isolatedHtml = useMemo(() => {
    const sanitized = stripSliderRuntimeState(hasPreviewGuard(html) ? html : sanitizeHtml(html));
    const scripted = scriptsStripped ? stripExecutableScripts(sanitized) : sanitized;
    return injectIsolationStyles(
      viewportHeight ? pinViewportUnits(scripted, viewportHeight) : scripted,
      scrollChaining
    );
  }, [html, viewportHeight, scriptsStripped, scrollChaining]);

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    // Wait for <body>: consumers key their effects on the document's identity,
    // so publishing a still-parsing, body-less document would freeze them on a
    // document they can never observe (the object identity never changes when
    // the body appears later).
    if (!iframe?.contentDocument?.body) return;

    // The page reloaded itself (see selfReloadedHtml): don't publish this
    // document — the script-less re-render replaces it.
    if (isSelfReload(iframe)) {
      freezeScripts();
      return;
    }

    const doc = iframe.contentDocument;
    setContentDocument(doc);

    // Out-of-flow layers the page's own CSS hides at this width, left visible
    // by a capture taken at another one (see enforceRestingVisibility).
    enforceRestingVisibility(doc);
    // A cookie banner the site built itself, which the CMP hider CSS cannot
    // name (see hideUnlabelledCookieBanners).
    hideUnlabelledCookieBanners(doc);

    // Catch the page navigating away the moment it starts (the poll above
    // would only see the reloaded document up to a tick later): the unload
    // of a document we did not replace ourselves is the page's doing.
    if (listenedDocumentRef.current !== doc) {
      listenedDocumentRef.current = doc;
      doc.defaultView?.addEventListener(
        'pagehide',
        () => {
          if (disposedRef.current) return;
          if (ownNavigationRef.current) {
            ownNavigationRef.current = false;
            return;
          }
          if (listenedDocumentRef.current !== doc) return;
          // Gone on its own: the next srcdoc write replaces nothing we listen to.
          listenedDocumentRef.current = null;
          freezeScripts();
        },
        { once: true }
      );
    }

    // Artboard mode: pin the external sheets' viewport units as they appear
    // (each link is handled once; late-added links get picked up by the
    // next tick / resize).
    if (viewportHeight) {
      pinExternalStylesheets(doc, viewportHeight);
      pinInlineStyleAttributes(doc, viewportHeight);
    }

    // Resize iframe to match content height (no internal scrollbar)
    setContentHeight(doc.body.scrollHeight);
  }, [freezeScripts, viewportHeight]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Replacing a document we listen to unloads it: that pagehide is ours.
    ownNavigationRef.current = listenedDocumentRef.current !== null;
    iframe.srcdoc = isolatedHtml;

    // Poll handleLoad every 500ms so the preview tracks content that streams
    // in during loading (images, fonts, late layout) instead of waiting for
    // the full load event. The load event runs one final handleLoad and stops
    // the polling; from there a ResizeObserver on the document keeps the
    // height in step with anything that lays out after load (lazy images,
    // fonts, a builder runtime sizing sections) — polling would have stopped
    // short of it.
    const intervalId = setInterval(handleLoad, 500);
    const resizeObserver = new ResizeObserver(handleLoad);

    const onLoad = () => {
      clearInterval(intervalId);
      handleLoad();
      const root = iframe.contentDocument?.documentElement;
      if (root) resizeObserver.observe(root);
    };
    iframe.addEventListener('load', onLoad);

    return () => {
      clearInterval(intervalId);
      resizeObserver.disconnect();
      iframe.removeEventListener('load', onLoad);
    };
  }, [isolatedHtml, handleLoad]);


  return { iframeRef, contentDocument, contentHeight };
}
